import { useState } from "react";

import { AD_TYPE_OPTIONS } from "../constants/filters";
import { COPY } from "../copy";
import type { InsightsChartMetric, InsightsCustomerSegment } from "../types";

import { AdsManagerContent } from "./AdsManagerContent";
import { useAdTypesModal } from "./AdTypesExplainer";
import { useIsCompact } from "./CardDensity";
import { CustomerInsightsSection } from "./customers/CustomerInsightsSection";
import { CustomersAcquiredTable } from "./customers/CustomersAcquiredTable";
import { InsightsFilterMenu } from "./InsightsFilterMenu";

export function CustomersTab({
  useTotalAdSales = false,
  defaultChartMetric = "ad-sales",
  customerValueAboveChart = false,
}: {
  useTotalAdSales?: boolean;
  defaultChartMetric?: InsightsChartMetric;
  customerValueAboveChart?: boolean;
}) {
  const [adType, setAdType] =
    useState<InsightsCustomerSegment>("all-customers");
  const openAdTypesModal = useAdTypesModal();
  const isCompact = useIsCompact();

  return (
    <AdsManagerContent className="py-8 pb-32">
      {/* One flat stack of cards, matching the Overview: the heading sits on the
          page and each block owns its own box. */}
      <div className="space-y-6">
        {/* Ad type scopes the tab. Date range moved into the one cumulative
            table it affects; the monthly chart and conversion list navigate
            their own history instead. */}
        <div className="flex flex-col gap-4 min-[733px]:flex-row min-[733px]:items-start min-[733px]:justify-between">
          <div>
            <h2 className="text-xl leading-8 font-medium text-[#333]">
              {COPY.customers.heading}
            </h2>
            <p
              className={`text-sm leading-5 text-[#757575] ${
                isCompact ? "whitespace-nowrap" : "max-w-[720px]"
              }`}
            >
              {isCompact ? COPY.customers.v3Subhead : COPY.customers.subhead}
            </p>
          </div>
          <div className="flex shrink-0 items-center">
            <InsightsFilterMenu
              name="customers-ad-type"
              labelPrefix="Ad type"
              menuTitle="Filter by ad type"
              value={adType}
              options={AD_TYPE_OPTIONS}
              onChange={setAdType}
              clearValue="all-customers"
              titleInfo={
                openAdTypesModal
                  ? {
                      label: COPY.overview.adTypesModal.title,
                      onClick: openAdTypesModal,
                    }
                  : undefined
              }
            />
          </div>
        </div>

        <CustomerInsightsSection
          customerSegment={adType}
          useTotalAdSales={useTotalAdSales}
          defaultChartMetric={defaultChartMetric}
          customerValueAboveChart={customerValueAboveChart}
        />

        <CustomersAcquiredTable customerSegment={adType} />
      </div>
    </AdsManagerContent>
  );
}
